import React, { useEffect, useState } from "react";
import {
  EuiButton,
  EuiButtonIcon,
  EuiCallOut,
  EuiFieldPassword,
  EuiFieldText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiLoadingSpinner,
  EuiSpacer,
  EuiText,
} from "@elastic/eui";
import { useApi } from "src/api";
import { useAnketaI18n } from "src/internationalization";
import { useToast } from "src/notifications";
import { useAnketaAuth } from "../auth/context";

interface SimarglTokenStatus {
  accessKey: string;
  exists: boolean;
  createdAt: string | null;
  endpoint: string;
  region: string;
  bucket: string;
}

interface SimarglTokenSecret extends SimarglTokenStatus {
  secretKey: string;
  generated: boolean;
  vaultMirrored: boolean;
}

/**
 * Admin view for the dedicated, lookup-only simargl S3 service credential
 * (`simargl.anketa`). most/simargl uses it to resolve any node's deployment
 * values by MAC/IP/hostname across questionnaires. Sudo-only (the whole Admin
 * area is gated by user.isSudo; the backend re-enforces it on every request).
 *
 * The secret is only ever returned by a create/rotate POST and is shown once -
 * the GET status call never reveals it.
 */
export const AdminSimarglCredential = (): JSX.Element => {
  const { Get, Post } = useApi();
  const { addToast } = useToast();
  const { translate } = useAnketaI18n();
  const { keycloak } = useAnketaAuth();

  const [loading, setLoading] = useState<boolean>(true);
  const [forbidden, setForbidden] = useState<boolean>(false);
  const [busy, setBusy] = useState<boolean>(false);
  const [status, setStatus] = useState<SimarglTokenStatus | null>(null);
  const [secret, setSecret] = useState<string | null>(null);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      addToast({
        title: translate("anketa.helper.copy.success.title", {
          defaultMessage: "Copied to clipboard",
        }),
        color: "success",
      });
    } catch (err) {
      console.error("Failed to copy text: ", err);
      addToast({
        title: translate("anketa.helper.copy.error.title", {
          defaultMessage: "Copy failed",
        }),
        color: "danger",
      });
    }
  };

  const loadStatus = async () => {
    setLoading(true);
    try {
      const resp = await Get<SimarglTokenStatus & { success: boolean }>({
        path: "v2/opsanio/simargl/service-token",
        responseType: "json",
        token: keycloak.token,
      });
      if (!resp.success && (resp.status === 403 || resp.status === 401)) {
        setForbidden(true);
        return;
      }
      if (resp.data) {
        setStatus(resp.data as SimarglTokenStatus);
      }
    } catch (err) {
      console.error("Failed to load simargl credential status:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createOrRotate = async (rotate: boolean) => {
    setBusy(true);
    try {
      const resp = await Post<SimarglTokenSecret & { success: boolean }>({
        path: "v2/opsanio/simargl/service-token",
        responseType: "json",
        token: keycloak.token,
        value: { rotate },
      });
      if (!resp.success || !resp.data?.secretKey) {
        throw new Error(resp.error?.message ?? "Request failed");
      }
      const data = resp.data as SimarglTokenSecret;
      setSecret(data.secretKey);
      setStatus({
        accessKey: data.accessKey,
        exists: true,
        createdAt: new Date().toISOString(),
        endpoint: data.endpoint,
        region: data.region,
        bucket: data.bucket,
      });
      addToast({
        title: rotate
          ? translate("anketa.admin.simargl.rotate.success.title", {
              defaultMessage: "Credential rotated",
            })
          : translate("anketa.admin.simargl.create.success.title", {
              defaultMessage: "Credential ready",
            }),
        color: "success",
        text: data.vaultMirrored
          ? translate("anketa.admin.simargl.vault.ok", {
              defaultMessage: "Mirrored to Vault at simargl/anketa-s3.",
            })
          : translate("anketa.admin.simargl.vault.skip", {
              defaultMessage:
                "Vault mirror was skipped or failed - copy the secret now.",
            }),
      });
    } catch (err: any) {
      console.error("Failed to create/rotate simargl credential:", err);
      addToast({
        title: translate("anketa.admin.simargl.error.title", {
          defaultMessage: "Operation failed",
        }),
        color: "danger",
        text: err?.message,
      });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <EuiLoadingSpinner size="l" />;
  }

  if (forbidden) {
    return (
      <EuiCallOut
        color="danger"
        iconType="lock"
        title={translate("anketa.admin.simargl.forbidden", {
          defaultMessage: "You do not have permission to manage this credential.",
        })}
      />
    );
  }

  const accessKey = status?.accessKey ?? "simargl.anketa";

  return (
    <>
      <EuiText size="s">
        <p>
          {translate("anketa.admin.simargl.intro", {
            defaultMessage:
              "Dedicated lookup-only S3 credential used by most/simargl to resolve node deployment values by MAC, IP or hostname. Provide the secret to the simargl chart (valuesS3.existingSecret) - it is shown only once here.",
          })}
        </p>
      </EuiText>
      <EuiSpacer size="m" />

      <EuiFormRow
        label={translate("anketa.admin.simargl.accessKey", {
          defaultMessage: "S3 Access Key",
        })}
      >
        <EuiFieldText
          prepend={
            <EuiButtonIcon
              iconType="copy"
              aria-label={translate("anketa.admin.simargl.accessKey.copy", {
                defaultMessage: "Copy access key",
              })}
              onClick={() => copyToClipboard(accessKey)}
            />
          }
          readOnly
          value={accessKey}
        />
      </EuiFormRow>

      <EuiFormRow
        label={translate("anketa.admin.simargl.secret", {
          defaultMessage: "S3 Secret Key",
        })}
        helpText={
          secret
            ? translate("anketa.admin.simargl.secret.once", {
                defaultMessage:
                  "Copy this now - it will not be shown again. Rotate to get a new one.",
              })
            : translate("anketa.admin.simargl.secret.hidden", {
                defaultMessage:
                  "The secret is never displayed after creation. Create or rotate to reveal a new value.",
              })
        }
      >
        <EuiFieldPassword
          type="dual"
          readOnly
          value={secret ?? ""}
          placeholder={
            status?.exists
              ? translate("anketa.admin.simargl.secret.exists", {
                  defaultMessage: "•••••••• (exists - rotate to reveal a new one)",
                })
              : translate("anketa.admin.simargl.secret.none", {
                  defaultMessage: "Not created yet",
                })
          }
          append={
            secret ? (
              <EuiButtonIcon
                iconType="copy"
                aria-label={translate("anketa.admin.simargl.secret.copy", {
                  defaultMessage: "Copy secret key",
                })}
                onClick={() => copyToClipboard(secret)}
              />
            ) : undefined
          }
        />
      </EuiFormRow>

      <EuiSpacer size="m" />
      <EuiFlexGroup gutterSize="s" responsive={false} wrap>
        <EuiFlexItem grow={false}>
          <EuiButton
            fill
            isLoading={busy}
            onClick={() => createOrRotate(false)}
          >
            {status?.exists
              ? translate("anketa.admin.simargl.reveal", {
                  defaultMessage: "Show current (create if missing)",
                })
              : translate("anketa.admin.simargl.create", {
                  defaultMessage: "Create credential",
                })}
          </EuiButton>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiButton
            color="warning"
            iconType="refresh"
            isLoading={busy}
            onClick={() => createOrRotate(true)}
          >
            {translate("anketa.admin.simargl.rotate", {
              defaultMessage: "Rotate secret",
            })}
          </EuiButton>
        </EuiFlexItem>
      </EuiFlexGroup>

      <EuiSpacer size="l" />
      <EuiText size="s">
        <h4>
          {translate("anketa.admin.simargl.chart.title", {
            defaultMessage: "Chart configuration (simargl values.yaml)",
          })}
        </h4>
      </EuiText>
      <EuiSpacer size="s" />
      <EuiFormRow
        label={translate("anketa.admin.simargl.endpoint", {
          defaultMessage: "Endpoint",
        })}
      >
        <EuiFieldText readOnly value={status?.endpoint ?? ""} />
      </EuiFormRow>
      <EuiFlexGroup gutterSize="m">
        <EuiFlexItem>
          <EuiFormRow
            label={translate("anketa.admin.simargl.bucket", {
              defaultMessage: "Bucket",
            })}
          >
            <EuiFieldText readOnly value={status?.bucket ?? "opsanio-values"} />
          </EuiFormRow>
        </EuiFlexItem>
        <EuiFlexItem>
          <EuiFormRow
            label={translate("anketa.admin.simargl.region", {
              defaultMessage: "Region",
            })}
          >
            <EuiFieldText readOnly value={status?.region ?? ""} />
          </EuiFormRow>
        </EuiFlexItem>
      </EuiFlexGroup>
    </>
  );
};
